// NEW FILE: client/src/services/subscriptionService.js
// SECURITY: Real-time subscription validation service

import { supabase } from '../lib/supabase';

class SubscriptionService {
  
  /**
   * Validate user's current subscription status
   * Checks both database and real-time Stripe status
   */
  async validateSubscriptionAccess(userId) {
    try {
      // Get user's profile with subscription data including freemium fields
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('subscription_status, trial_ends_at, stripe_customer_id, subscription_id, plan_type, monthly_quotes_used, free_quotes_limit')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Failed to fetch subscription data:', error);
        return {
          hasAccess: false,
          reason: 'Unable to verify subscription',
          requiresPayment: true
        };
      }

      // Handle freemium model
      if (profile.plan_type === 'free' || !profile.subscription_status || profile.subscription_status === 'free') {
        // Free plan users always have access (with quota limitations)
        return {
          hasAccess: true,
          reason: 'Free plan active',
          requiresPayment: false,
          planType: 'free',
          monthlyQuotesUsed: profile.monthly_quotes_used || 0,
          freeQuotesLimit: profile.free_quotes_limit || 3
        };
      }

      // Legacy trial handling (for existing users)
      if (profile.subscription_status === 'trialing') {
        // Check if trial is expired
        if (profile.trial_ends_at) {
          const trialExpired = new Date() > new Date(profile.trial_ends_at);
          if (trialExpired) {
            return {
              hasAccess: false,
              reason: 'Free trial has expired',
              requiresPayment: true
            };
          }
        }
        
        // Trial is still valid
        return {
          hasAccess: true,
          reason: 'Free trial active',
          requiresPayment: false,
          trialEndsAt: profile.trial_ends_at
        };
      }

      // Active subscription (paid plan)
      if (profile.subscription_status === 'active') {
        return {
          hasAccess: true,
          reason: 'Active subscription',
          requiresPayment: false,
          planType: 'paid'
        };
      }

      // Cancelled, past_due, or other inactive status
      return {
        hasAccess: false,
        reason: `Subscription ${profile.subscription_status}`,
        requiresPayment: true
      };

    } catch (error) {
      console.error('Subscription validation error:', error);
      return {
        hasAccess: false,
        reason: 'Subscription verification failed',
        requiresPayment: true
      };
    }
  }

  /**
   * Check if user needs to complete onboarding
   */
  async checkOnboardingStatus(userId) {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('onboarding_completed, trial_setup_completed, onboarding_step')
        .eq('id', userId)
        .single();

      if (error) {
        return {
          needsOnboarding: true,
          needsTrialSetup: true,
          nextStep: '/onboarding/welcome'
        };
      }

      if (!profile.onboarding_completed) {
        return {
          needsOnboarding: true,
          needsTrialSetup: true,
          nextStep: '/onboarding/welcome'
        };
      }

      if (!profile.trial_setup_completed) {
        return {
          needsOnboarding: false,
          needsTrialSetup: true,
          nextStep: '/onboarding/trial-setup'
        };
      }

      // Onboarding complete, check subscription access
      const subscriptionStatus = await this.validateSubscriptionAccess(userId);
      
      if (!subscriptionStatus.hasAccess) {
        return {
          needsOnboarding: false,
          needsTrialSetup: true,
          nextStep: '/onboarding/trial-setup',
          reason: subscriptionStatus.reason
        };
      }

      return {
        needsOnboarding: false,
        needsTrialSetup: false,
        nextStep: '/dashboard'
      };

    } catch (error) {
      console.error('Onboarding status check error:', error);
      return {
        needsOnboarding: true,
        needsTrialSetup: true,
        nextStep: '/onboarding/welcome'
      };
    }
  }

  /**
   * Update subscription status after Stripe verification
   */
  async updateSubscriptionStatus(userId, subscriptionData) {
    try {
      const updateData = {
        subscription_status: subscriptionData.status,
        stripe_customer_id: subscriptionData.customerId,
        subscription_id: subscriptionData.subscriptionId,
        trial_setup_completed: true,
        trial_setup_completed_at: new Date().toISOString()
      };

      // Set trial end date if in trial
      if (subscriptionData.status === 'trialing' && subscriptionData.trialEnd) {
        updateData.trial_ends_at = new Date(subscriptionData.trialEnd * 1000).toISOString();
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        throw error;
      }

      return { success: true };

    } catch (error) {
      console.error('Failed to update subscription status:', error);
      throw error;
    }
  }

  /**
   * Get user's subscription info for display
   */
  async getSubscriptionInfo(userId) {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('subscription_status, trial_ends_at, created_at, plan_type, monthly_quotes_used, free_quotes_limit')
        .eq('id', userId)
        .single();

      if (error) {
        throw error;
      }

      const subscriptionStatus = await this.validateSubscriptionAccess(userId);
      
      return {
        status: profile.subscription_status || 'none',
        trialEndsAt: profile.trial_ends_at,
        hasAccess: subscriptionStatus.hasAccess,
        displayStatus: this.getDisplayStatus(profile, subscriptionStatus),
        daysRemaining: this.calculateDaysRemaining(profile.trial_ends_at),
        planType: subscriptionStatus.planType || 'free',
        monthlyQuotesUsed: subscriptionStatus.monthlyQuotesUsed || 0,
        freeQuotesLimit: subscriptionStatus.freeQuotesLimit || 3
      };

    } catch (error) {
      console.error('Failed to get subscription info:', error);
      return {
        status: 'unknown',
        hasAccess: false,
        displayStatus: 'Unable to verify subscription'
      };
    }
  }

  /**
   * Get user-friendly status display
   */
  getDisplayStatus(profile, subscriptionStatus) {
    if (subscriptionStatus.hasAccess) {
      if (subscriptionStatus.planType === 'free') {
        return 'Free Plan';
      }
      if (profile.subscription_status === 'trialing') {
        return 'Free Trial';
      }
      if (subscriptionStatus.planType === 'paid') {
        return 'Paid Plan';
      }
      return 'Paid Account';
    }
    
    return 'Payment Required';
  }

  /**
   * Calculate days remaining in trial
   */
  calculateDaysRemaining(trialEndsAt) {
    if (!trialEndsAt) return null;
    
    const now = new Date();
    const trialEnd = new Date(trialEndsAt);
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  /**
   * Initiate subscription cancellation process
   * Returns retention offer or confirmation of cancellation
   */
  async requestCancellation(cancellationData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          cancellationReason: cancellationData.reason,
          cancellationFeedback: cancellationData.feedback,
          skipRetention: cancellationData.skipRetention || false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process cancellation request');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Cancellation request error:', error);
      throw error;
    }
  }

  /**
   * Accept a retention offer
   */
  async acceptRetentionOffer(retentionOfferType, cancellationReason) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          cancellationReason: cancellationReason,
          acceptRetentionOffer: true,
          retentionOfferType: retentionOfferType,
          skipRetention: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to apply retention offer');
      }

      return await response.json();

    } catch (error) {
      console.error('Retention offer acceptance error:', error);
      throw error;
    }
  }

  /**
   * Decline retention offer and proceed with cancellation
   */
  async declineRetentionOffer(cancellationReason, cancellationFeedback) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          cancellationReason: cancellationReason,
          cancellationFeedback: cancellationFeedback,
          skipRetention: true,
          acceptRetentionOffer: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      }

      return await response.json();

    } catch (error) {
      console.error('Subscription cancellation error:', error);
      throw error;
    }
  }

  /**
   * Get available cancellation reasons with descriptions
   */
  getCancellationReasons() {
    return [
      {
        value: 'too_expensive',
        label: 'Too expensive',
        description: 'The subscription cost is too high for my budget'
      },
      {
        value: 'not_using_enough',
        label: 'Not using it enough',
        description: 'I\'m not getting enough value from the features'
      },
      {
        value: 'missing_features',
        label: 'Missing features',
        description: 'The product doesn\'t have features I need'
      },
      {
        value: 'switching_competitor',
        label: 'Switching to competitor',
        description: 'Found a better alternative solution'
      },
      {
        value: 'business_closed',
        label: 'Business closed',
        description: 'My business is no longer operating'
      },
      {
        value: 'temporary_pause',
        label: 'Temporary pause',
        description: 'Taking a break but may return later'
      },
      {
        value: 'technical_issues',
        label: 'Technical issues',
        description: 'Having problems with the software'
      },
      {
        value: 'other',
        label: 'Other',
        description: 'Different reason (please explain below)'
      }
    ];
  }

  /**
   * Format retention offer for display
   */
  formatRetentionOffer(retentionOffer) {
    if (!retentionOffer) return null;

    const baseOffer = {
      title: retentionOffer.title,
      description: retentionOffer.description,
      savings: retentionOffer.savings,
      type: retentionOffer.type
    };

    // Add call-to-action based on offer type
    switch (retentionOffer.type) {
      case 'discount_50_3months':
        baseOffer.buttonText = 'Accept 50% Discount';
        baseOffer.details = 'Your next 3 invoices will be 50% off';
        break;
      case 'discount_25_6months':
        baseOffer.buttonText = 'Accept 25% Discount';
        baseOffer.details = 'Your next 6 invoices will be 25% off';
        break;
      case 'pause_1month':
        baseOffer.buttonText = 'Pause for 1 Month';
        baseOffer.details = 'Your subscription will resume automatically';
        break;
      case 'pause_3months':
        baseOffer.buttonText = 'Pause for 3 Months';
        baseOffer.details = 'Your subscription will resume automatically';
        break;
      default:
        baseOffer.buttonText = 'Accept Offer';
    }

    return baseOffer;
  }
}

export const subscriptionService = new SubscriptionService();

// USAGE EXAMPLES:

// In components:
// const subscriptionStatus = await subscriptionService.validateSubscriptionAccess(user.id);
// if (!subscriptionStatus.hasAccess) {
//   // Redirect to payment
// }

// In onboarding success:
// await subscriptionService.updateSubscriptionStatus(user.id, {
//   status: 'trialing',
//   customerId: 'cus_xxx',
//   subscriptionId: 'sub_xxx',
//   trialEnd: 1234567890
// });

// In profile/dashboard:
// const subscriptionInfo = await subscriptionService.getSubscriptionInfo(user.id);
// console.log(`Status: ${subscriptionInfo.displayStatus}`);

// For cancellation:
// const cancellationResult = await subscriptionService.requestCancellation({
//   reason: 'too_expensive',
//   feedback: 'Cannot afford the monthly cost'
// });
// if (cancellationResult.showRetentionOffer) {
//   // Show retention offer UI
// }
